import React, { useEffect, useState } from 'react';
import api from '../config/api';
import {
  Download,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  FileText,
  Loader2,
  MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import Portal from '../components/Portal';
import { useAction } from '../contexts/ActionContext';

// ErrorBoundary component
function ErrorBoundary({ children }) {
  const [error, setError] = useState(null);
  if (error) {
    return <div className="text-red-600 text-center mt-8">An error occurred: {error.message || String(error)}</div>;
  }
  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      {React.cloneElement(children, { onError: setError })}
    </React.Suspense>
  );
}

export default function DownloadLists() {
  const { runAction } = useAction();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 10;
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState([]);

  // Search state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchJobs = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const res = await api.get('/export/jobs');
      setJobs(res.data.data || []);
      setError(null);
    } catch (e) {
      setError('Failed to load export jobs');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setJobToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!jobToDelete) return;
    setDeletingId(jobToDelete);
    setDeleteModalOpen(false);
    await runAction(async () => {
      await api.delete(`/export/jobs/${jobToDelete}`);
      toast.success('List deleted successfully');
      await fetchJobs(false);
    }, { message: 'Deleting list...' });
    setDeletingId(null);
    setJobToDelete(null);
  };

  const handleDownload = async (jobId) => {
    if (isDownloading) return;

    setIsDownloading(true);
    setDownloadingJobId(jobId);

    try {
      // Use native browser download (not axios blob) to avoid
      // Chrome net::ERR_FAILED when Apache mod_deflate or range
      // handling causes Content-Length mismatch on large files.
      const apiToken = localStorage.getItem('api_token') || '';
      const link = document.createElement('a');
      link.href = `/api/v1/export/download/${jobId}?token=${encodeURIComponent(apiToken)}`;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
      setDownloadingJobId(null);
    }
  };

  const handleBatchDownload = async () => {
    const completedSelected = jobs.filter(j => selectedIds.includes(j.id) && j.status === 'completed');
    if (completedSelected.length === 0) {
      toast.error('No completed jobs selected for download');
      return;
    }

    setIsDownloading(true);
    setDownloadingJobId('batch');
    setDownloadProgress(0);

    const idsQuery = completedSelected.map(j => j.id).join(',');

    await runAction(async () => {
      // Use native browser download (not blob) to avoid Chrome
      // net::ERR_FAILED on large downloads through Apache proxy.
      const apiToken = localStorage.getItem('api_token') || '';
      const link = document.createElement('a');
      link.href = `/api/v1/export/download-zip?ids=${idsQuery}&token=${encodeURIComponent(apiToken)}`;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Zip download started');
      setSelectedIds([]);
    }, { message: 'Creating zip...' });

    setIsDownloading(false);
    setDownloadingJobId(null);
    setDownloadProgress(0);
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} selected lists?`)) return;
    if (isBatchDeleting) return;

    setIsBatchDeleting(true);
    await runAction(async () => {
      for (const id of selectedIds) {
        await api.delete(`/export/jobs/${id}`);
      }
      toast.success('Selected lists deleted');
      setSelectedIds([]);
      await fetchJobs(true);
    }, { message: `Deleting ${selectedIds.length} list(s)...` });
    setIsBatchDeleting(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedJobs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedJobs.map(j => j.id));
    }
  };

  useEffect(() => {
    fetchJobs(true);
    const interval = setInterval(() => {
      fetchJobs(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);


  const filteredJobs = jobs.filter(job => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return true;
    let tags = [];
    try { tags = job.tags ? JSON.parse(job.tags) : []; } catch { tags = []; }
    return (
      (job.job_name && job.job_name.toLowerCase().includes(q)) ||
      (job.list_id && String(job.list_id).toLowerCase().includes(q)) ||
      tags.some(tag => tag.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const paginatedJobs = filteredJobs.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-black text-gray-900 dark:text-gray-100 tracking-tight flex items-center gap-3">
              <Download className="h-10 w-10 text-blue-600" />
              Download Lists
            </h1>
            <p className="mt-2 text-gray-500 dark:text-gray-400 text-lg">Manage and download your exported verification lists.</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const allCompleted = jobs.filter(j => j.status === 'completed').map(j => j.id);
                if (allCompleted.length === 0) return toast.error('No completed lists to download');
                setSelectedIds(allCompleted);
                handleBatchDownload();
              }}
              className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all border border-gray-200 dark:border-gray-700"
            >
              <Download className="h-4 w-4" /> Download All
            </button>
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                className="input pl-10 pr-4 py-2.5 w-full md:w-80 rounded-xl border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                placeholder="Search exports..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>
        </div>

        {/* Action Bar */}
        {selectedIds.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-900/20 animate-fadeIn">
            <span className="text-blue-700 dark:text-blue-300 font-semibold px-2">{selectedIds.length} items selected</span>
            <div className="h-6 w-px bg-blue-200 dark:bg-blue-800 mx-2" />
            <button
              onClick={handleBatchDownload}
              disabled={isDownloading}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              {isDownloading && downloadingJobId === 'batch' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {downloadProgress > 0 ? `Downloading ${downloadProgress}%` : 'Downloading...'}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" /> Download Selected
                </>
              )}
            </button>
            <button
              onClick={handleBatchDelete}
              disabled={isBatchDeleting}
              className={`btn btn-sm flex items-center gap-2 border-0 shadow-none ${
                isBatchDeleting
                  ? 'bg-red-200 text-red-300 cursor-not-allowed dark:bg-red-900/10 dark:text-red-500/50'
                  : 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
              }`}
            >
              {isBatchDeleting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="h-4 w-4" /> Delete Selected</>
              )}
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium ml-auto"
            >
              Deselect All
            </button>
          </div>
        )}

        {loading && !jobs.length ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading your exports...</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {/* Header for list */}
            <div className="hidden md:flex items-center px-6 py-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
              <div className="w-10">
                <button onClick={toggleSelectAll} className="text-gray-400 hover:text-blue-600 transition-colors">
                  {selectedIds.length === paginatedJobs.length && paginatedJobs.length > 0 ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
                </button>
              </div>
              <div className="flex-1">List Details</div>
              <div className="w-48 text-center">Status & Progress</div>
              <div className="w-48 text-right">Actions</div>
            </div>

            {paginatedJobs.map(job => {
              let tags = [];
              let filters = {};
              if (job.tags) {
                try { tags = typeof job.tags === 'string' ? JSON.parse(job.tags) : job.tags; } catch { tags = []; }
              }
              if (job.filters) {
                try { filters = typeof job.filters === 'string' ? JSON.parse(job.filters) : job.filters; } catch { filters = {}; }
              }

              const isDownloadingThis = downloadingJobId === job.id;

              return (
                <div
                  key={job.id}
                  className={`group border rounded-2xl p-4 md:p-6 bg-white dark:bg-gray-900 transition-all hover:shadow-xl hover:border-blue-200 dark:hover:border-blue-900/50 flex flex-col md:flex-row md:items-center gap-4 ${selectedIds.includes(job.id) ? 'border-blue-600 ring-2 ring-blue-600/10' : 'border-gray-100 dark:border-gray-800'}`}
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => toggleSelect(job.id)}
                      className={`transition-colors ${selectedIds.includes(job.id) ? 'text-blue-600' : 'text-gray-300 group-hover:text-gray-400'}`}
                    >
                      {selectedIds.includes(job.id) ? <CheckSquare className="h-6 w-6" /> : <Square className="h-6 w-6" />}
                    </button>

                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 truncate">{job.job_name}</h3>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded text-[10px] font-mono uppercase tracking-tight">#{job.id}</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        {job.list_id === 'ALL' ? 'Everything' : `List: ${job.list_id}`}
                      </span>
                      <span className="hidden md:block text-gray-300">•</span>
                      <span className="whitespace-nowrap">{new Date(job.created_at).toLocaleDateString()} at {new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium border border-blue-100/50 dark:border-blue-900/30">{tag}</span>
                      ))}
                      {filters.types && (
                        <span className="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-md text-xs font-medium border border-purple-100/50 dark:border-purple-900/30">
                          {filters.types.includes('ALL') ? 'All' : filters.types.includes('ALL_DELIVERABLES') ? 'Deliverables' : filters.types[0]}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full md:w-48">
                    <div className="flex items-center justify-between md:justify-center gap-2 mb-2">
                      <div className="flex items-center gap-1.5">
                        {getStatusIcon(job.status)}
                        <span className="text-xs font-bold uppercase tracking-wider">{job.status}</span>
                      </div>
                      <span className="text-xs font-mono text-gray-400 bg-gray-50 dark:bg-gray-800 px-1.5 py-0.5 rounded md:hidden">{Math.round((job.progress / (job.total || 1)) * 100)}%</span>
                    </div>

                    {/* Background generation progress */}
                    {job.status === 'processing' && (
                      <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full transition-all duration-1000"
                          style={{ width: `${Math.max(5, (job.progress / (job.total || 1)) * 100)}%` }}
                        />
                      </div>
                    )}

                    <div className="text-[10px] text-gray-400 text-center mt-1 uppercase tracking-tighter font-semibold">
                      {job.progress} / {job.total} records
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 w-full md:w-48">
                    {job.status === 'completed' && job.file_path && (
                      <div className="relative flex-1">
                        <button
                          onClick={() => handleDownload(job.id)}
                          disabled={isDownloading}
                          className={`btn btn-primary w-full py-2.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all ${isDownloadingThis ? 'cursor-not-allowed overflow-hidden' : ''}`}
                        >
                          {isDownloadingThis ? (
                            <span className="relative z-10 flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" /> {downloadProgress > 0 ? `${downloadProgress}%` : 'Downloading...'}
                            </span>
                          ) : (
                            <>
                              <Download className="h-4 w-4" /> Download
                            </>
                          )}

                          {/* Inner download progress bar overlay */}
                          {isDownloadingThis && (
                            <div
                              className="absolute inset-0 bg-blue-700 transition-all duration-300"
                              style={{ width: `${downloadProgress}%` }}
                            />
                          )}
                        </button>
                      </div>
                    )}

                    <button
                      className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                      onClick={() => handleDelete(job.id)}
                      title="Delete Export"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {filteredJobs.length === 0 && (
              <div className="text-center py-20 bg-gray-50 dark:bg-gray-900/30 rounded-3xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">No exports found</h3>
                <p className="text-gray-500 mt-1">Try adjusting your search or export a new list.</p>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            <button
              className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 transition-all"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`w-10 h-10 rounded-xl font-bold transition-all ${currentPage === i + 1 ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              className="p-3 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 transition-all"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <Portal><div className="modal-overlay">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 max-w-sm w-full animate-scaleIn border border-gray-100 dark:border-gray-800">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mb-6">
                <Trash2 className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">Delete Export?</h2>
              <p className="mb-8 text-gray-600 dark:text-gray-400">This file will be permanently removed from the server. This action cannot be reversed.</p>
              <div className="flex gap-3">
                <button
                  className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                  onClick={() => { setDeleteModalOpen(false); setJobToDelete(null); }}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 shadow-lg shadow-red-500/20 transition-all"
                  onClick={confirmDelete}
                  disabled={deletingId === jobToDelete}
                >
                  {deletingId === jobToDelete ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    </ErrorBoundary>
  );
}
